import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsAPI } from '../api/notifications.api';
import type { NotificationFilters } from '../types';

/**
 * Hook para buscar notificações
 */
export function useNotifications(filters?: NotificationFilters) {
  return useQuery({
    queryKey: ['notifications', filters],
    queryFn: async () => {
      const response = await notificationsAPI.list(filters);
      return response.data;
    },
    refetchInterval: 30000, // Refetch a cada 30 segundos
  });
}

/**
 * Hook para obter contagem de notificações não lidas
 */
export function useUnreadNotificationsCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await notificationsAPI.getUnreadCount();
      return response.data.data.count;
    },
    refetchInterval: 15000, // Refetch a cada 15 segundos para manter o badge atualizado
  });
}

/**
 * Hook para obter estatísticas de notificações
 */
export function useNotificationStats() {
  return useQuery({
    queryKey: ['notifications', 'stats'],
    queryFn: async () => {
      const response = await notificationsAPI.getStats();
      return response.data.data;
    },
  });
}

/**
 * Hook para marcar notificação como lida
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsAPI.markAsRead(id),
    onSuccess: () => {
      // Invalidar todas as queries de notificações
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook para marcar todas as notificações como lidas
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsAPI.markAllAsRead(),
    onSuccess: () => {
      // Invalidar todas as queries de notificações
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook para deletar notificação
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsAPI.delete(id),
    onSuccess: () => {
      // Invalidar todas as queries de notificações
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook para deletar todas as notificações
 */
export function useDeleteAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationsAPI.deleteAll(),
    onSuccess: () => {
      // Invalidar todas as queries de notificações
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
